<template>
  <div class="fq-root">

    <!-- Trigger link -->
    <button v-if="!open && !submitted" class="fq-trigger" @click="open = true">
      <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
        <path d="M4 15s1-1 4-1 5 2 8 2 4-1 4-1V3s-1 1-4 1-5-2-8-2-4 1-4 1z"/><line x1="4" y1="22" x2="4" y2="15"/>
      </svg>
      Report an issue
    </button>

    <!-- Confirmation -->
    <div v-else-if="submitted" class="fq-confirm">
      <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
        <polyline points="20 6 9 17 4 12"/>
      </svg>
      Thanks — this question has been flagged.
    </div>

    <!-- Issue panel -->
    <div v-else class="fq-panel">
      <div class="fq-panel__top">
        <span class="fq-panel__label">What's the issue?</span>
        <button class="fq-dismiss" @click="open = false" aria-label="Cancel">
          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
        </button>
      </div>

      <div class="fq-chips">
        <button
          v-for="opt in ISSUE_OPTIONS"
          :key="opt.value"
          class="fq-chip"
          :class="{ 'fq-chip--active': selectedIssue === opt.value }"
          :disabled="submitting"
          @click="selectIssue(opt.value)"
        >
          {{ opt.label }}
        </button>
      </div>

      <!-- "Other" text box -->
      <template v-if="selectedIssue === 'other'">
        <textarea
          v-model="otherText"
          class="fq-other-input"
          placeholder="Describe the issue briefly…"
          rows="2"
          maxlength="500"
        />
        <button
          class="fq-submit-btn"
          :disabled="!otherText.trim() || submitting"
          @click="submitFlag"
        >
          {{ submitting ? 'Submitting…' : 'Submit' }}
        </button>
      </template>

      <p v-if="submitError" class="fq-error">{{ submitError }}</p>
    </div>

  </div>
</template>

<script setup>
import { ref } from 'vue';
import { useUserStore } from '@/stores/userStore';
import { submitQuestionFlag } from '@/services/flagService';
import { useAnalytics } from '@/composables/useAnalytics';

const props = defineProps({
  question: { type: Object, required: true },
  childId:  { type: String, default: '' },
});

const ISSUE_OPTIONS = [
  { value: 'question_text',    label: 'Question text' },
  { value: 'answer_options',   label: 'Answer options' },
  { value: 'correct_answer',   label: 'Correct answer' },
  { value: 'solution',         label: 'Solution' },
  { value: 'image_diagram',    label: 'Image / diagram' },
  { value: 'other',            label: 'Other' },
];

const userStore   = useUserStore();
const { track }  = useAnalytics();
const open        = ref(false);
const submitted   = ref(false);
const submitting  = ref(false);
const submitError = ref('');
const selectedIssue = ref('');
const otherText     = ref('');

function selectIssue(value) {
  selectedIssue.value = value;
  if (value !== 'other') {
    submitFlag();
  }
}

async function submitFlag() {
  if (submitting.value) return;
  submitting.value = true;
  submitError.value = '';
  try {
    const session = userStore.session || {};
    await submitQuestionFlag({
      questionId:   props.question._id,
      issueType:    selectedIssue.value,
      description:  selectedIssue.value === 'other' ? otherText.value.trim() : '',
      childId:      props.childId || null,
      accountEmail: session.email || null,
    });
    submitted.value = true;
    track('question_issue_reported', {
      question_id: props.question._id,
      issue_type:  selectedIssue.value,
    });
  } catch {
    submitError.value = 'Could not submit — please try again.';
    selectedIssue.value = '';
  } finally {
    submitting.value = false;
  }
}
</script>

<style>
.fq-root {
  margin-top: 10px;
}

/* Trigger */
.fq-trigger {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  background: none;
  border: none;
  padding: 0;
  font-size: 11px;
  color: #f97316;
  cursor: pointer;
  transition: color 0.15s;
  font-family: inherit;
}
.fq-trigger:hover { color: #dc2626; }
.fq-trigger svg { flex-shrink: 0; }

/* Confirmation */
.fq-confirm {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  font-size: 11px;
  color: #16a34a;
  font-weight: 600;
}
.fq-confirm svg { flex-shrink: 0; stroke: #16a34a; }

/* Panel */
.fq-panel {
  background: #f9fafb;
  border: 1px solid #e5e7eb;
  border-radius: 10px;
  padding: 12px;
  display: flex;
  flex-direction: column;
  gap: 10px;
  max-width: 480px;
}
.fq-panel__top {
  display: flex;
  align-items: center;
  justify-content: space-between;
}
.fq-panel__label {
  font-size: 12px;
  font-weight: 700;
  color: #374151;
}
.fq-dismiss {
  background: none;
  border: none;
  color: #9ca3af;
  cursor: pointer;
  padding: 2px;
  display: flex;
  align-items: center;
  border-radius: 4px;
  transition: color 0.1s;
}
.fq-dismiss:hover { color: #374151; }

/* Chips */
.fq-chips {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
}
.fq-chip {
  background: #fff;
  border: 1.5px solid #d1d5db;
  border-radius: 9999px;
  padding: 5px 12px;
  font-size: 12px;
  font-weight: 500;
  color: #374151;
  cursor: pointer;
  transition: border-color 0.15s, background 0.15s, color 0.15s;
  font-family: inherit;
}
.fq-chip:hover:not(:disabled) {
  border-color: #6366f1;
  color: #4f46e5;
  background: #eef2ff;
}
.fq-chip--active {
  border-color: #6366f1;
  background: #eef2ff;
  color: #4f46e5;
}
.fq-chip:disabled { opacity: 0.5; cursor: not-allowed; }

/* Other text box */
.fq-other-input {
  width: 100%;
  border: 1.5px solid #d1d5db;
  border-radius: 7px;
  padding: 7px 10px;
  font-size: 12px;
  font-family: inherit;
  color: #111827;
  resize: none;
  outline: none;
  background: #fff;
  box-sizing: border-box;
  line-height: 1.5;
  transition: border-color 0.15s;
}
.fq-other-input:focus { border-color: #6366f1; }

.fq-submit-btn {
  align-self: flex-start;
  background: #4f46e5;
  color: #fff;
  border: none;
  border-radius: 7px;
  padding: 6px 16px;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  font-family: inherit;
  transition: opacity 0.15s;
}
.fq-submit-btn:hover:not(:disabled) { opacity: 0.85; }
.fq-submit-btn:disabled { opacity: 0.45; cursor: not-allowed; }

.fq-error {
  font-size: 11px;
  color: #ef4444;
  margin: 0;
}
</style>
